import React, { forwardRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faBullhorn,
  faCalendarCheck,
  faShoppingBasket,
  faShoppingCart,
  faHome,
  faComments,
  faRightFromBracket,
  faUserCircle,
  faUserPlus,
  faUserShield,
  faReceipt,
  faFileContract,
  faHandshake,
} from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../auth/AuthContext';
import { hasAdminGroup } from '../../auth/admin';
import './index.css';

interface NavbarProps {
  className?: string;
}

const Navbar = forwardRef<HTMLElement, NavbarProps>((props, ref) => {
  const { cart } = useCart();
  const { isAuthenticated, logout, profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // 1. Calculate the total
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleScheduleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === '/schedule') {
      event.preventDefault();
      navigate('/schedule', {
        replace: true,
        state: {
          calendarRefresh: Date.now(),
        },
      });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const navClassName = ['hogcloud-navbar', props.className].filter(Boolean).join(' ');
  const profileLabel = profile?.name || profile?.email || 'Profile';
  const canViewAdmin = hasAdminGroup(profile?.groups);

  return (
    <nav ref={ref} className={navClassName}>
      <div className="nav-top-row">
        <ul className="nav-pill nav-left">
          <li>
            <NavLink to="/" className={({ isActive }) => isActive ? 'active logo-link' : 'logo-link'}>
              <FontAwesomeIcon icon={faHome} className="hc_nav_icon" />
            </NavLink>
          </li>
          <li>
            <NavLink to="/shop" className={({ isActive }) => isActive ? 'active' : ''}>
              <FontAwesomeIcon icon={faShoppingBasket} className="hc_nav_icon" />
            </NavLink>
          </li>
          <li>
            <NavLink to="/about" className={({ isActive }) => isActive ? 'active' : ''}>
              <FontAwesomeIcon icon={faInfoCircle} className="hc_nav_icon" />
            </NavLink>
          </li>
          <li>
            <NavLink to="/contact" className={({ isActive }) => isActive ? 'active' : ''} aria-label="Contact Us" title="Contact Us">
              <FontAwesomeIcon icon={faBullhorn} className="hc_nav_icon" />
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/schedule"
              className={({ isActive }) => isActive ? 'active' : ''}
              aria-label="Schedule Appointment"
              title="Schedule Appointment"
              onClick={handleScheduleClick}
            >
              <FontAwesomeIcon icon={faCalendarCheck} className="hc_nav_icon" />
            </NavLink>
          </li>
        </ul>
        <ul className="nav-pill nav-right">
          <li className="profile-menu">
            <button
              className={`profile-menu-trigger ${isAuthenticated ? 'profile-active' : ''}`}
              type="button"
              aria-label="Member profile"
              title="Member profile"
            >
              <FontAwesomeIcon icon={faUserCircle} className="hc_nav_icon" />
            </button>
            <div className="profile-menu-panel">
              {isAuthenticated ? (
                <>
                  <span className="profile-menu-label">{profileLabel}</span>
                  <NavLink to="/welcome">
                    <FontAwesomeIcon icon={faHome} />
                    Member Home
                  </NavLink>
                  <NavLink to="/#arkiebyte">
                    <FontAwesomeIcon icon={faComments} />
                    Chat With Us
                  </NavLink>
                  <NavLink to="/profile">
                    <FontAwesomeIcon icon={faUserCircle} />
                    Profile
                  </NavLink>
                  <NavLink to="/orders">
                    <FontAwesomeIcon icon={faReceipt} />
                    Orders
                  </NavLink>
                  <NavLink to="/proposals">
                    <FontAwesomeIcon icon={faFileContract} />
                    Proposals
                  </NavLink>
                  <NavLink to="/meetings">
                    <FontAwesomeIcon icon={faHandshake} />
                    Meetings
                  </NavLink>
                  {canViewAdmin && (
                    <NavLink to="/admin">
                      <FontAwesomeIcon icon={faUserShield} />
                      Admin
                    </NavLink>
                  )}
                  <button type="button" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faRightFromBracket} />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <NavLink to="/#arkiebyte">
                    <FontAwesomeIcon icon={faComments} />
                    Chat With Us
                  </NavLink>
                  <NavLink to="/login">
                    <FontAwesomeIcon icon={faUserCircle} />
                    Login
                  </NavLink>
                  <NavLink to="/enroll">
                    <FontAwesomeIcon icon={faUserPlus} />
                    Enroll
                  </NavLink>
                </>
              )}
            </div>
          </li>
          <li>
            <NavLink 
              to="/cart" 
              className={({ isActive }) => {
                const baseClasses = "cart-link cart-link-expand";
                const activeClass = isActive ? "active" : "";
                const hasItemsClass = cart.length > 0 ? "cart-has-items" : "";
                
                return `${baseClasses} ${activeClass} ${hasItemsClass}`.trim();
              }}
            >
              <FontAwesomeIcon
                icon={faShoppingCart}
                className={`hc_nav_icon ${cart.length > 0 ? 'cart-glow' : ''}`}
              />
              {/* Only show the span if there is a total to display */}
              {total > 0 && (
                <span style={{ marginLeft: '6px' }}>
                  ${total.toFixed(2)}
                </span>
              )}
            </NavLink>
          </li>
        </ul>
      </div>
    </nav>
  );
});


Navbar.displayName = 'Navbar';

export default Navbar;
